import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissoes } from './PermissoesContext';
import { useAuth } from './AuthContext';

// ── Importações das páginas ────────────────────────────────────────
import Dashboard              from '../pages/Dashboard';
import AssistenteIA           from '../pages/AssistenteIA';
import FalarComSuporte        from '../pages/FalarComSuporte';
import Produtos               from '../pages/Produtos';
import CadastroProdutos       from '../pages/CadastroProdutos';
import TabelasPreco           from '../pages/TabelasPreco';
import AlteracaoMassaProdutos from '../pages/AlteracaoMassaProdutos';
import CatalogoOnline         from '../pages/CatalogoOnline';
import MercadoPagoConfig      from '../pages/MercadoPagoConfig';
import InstagramGestao        from '../pages/InstagramGestao';
import FidelidadeConfig       from '../pages/FidelidadeConfig';
import ExtratoFidelidade      from '../pages/ExtratoFidelidade';
import ExtratoGeralFidelidade from '../pages/ExtratoGeralFidelidade';
import Clientes               from '../pages/Clientes';
import CadastroClientes       from '../pages/CadastroClientes';
import Vendas                 from '../pages/Vendas';
import PedidosOrcamentos      from '../pages/PedidosOrcamentos';
import Estoque                from '../pages/Estoque';
import ManutencaoEstoque      from '../pages/ManutencaoEstoque';
import Fornecedores           from '../pages/Fornecedores';
import ListaFornecedores      from '../pages/ListaFornecedores';
import GruposMarcas           from '../pages/GruposMarcas';
import EntradaNF              from '../pages/EntradaNF';
import FechamentoCaixa        from '../pages/FechamentoCaixa';
import TransferenciaContas    from '../pages/TransferenciaContas';
import BaixaCartoes           from '../pages/BaixaCartoes';
import ContasPagar            from '../pages/ContasPagar';
import ContasReceber          from '../pages/ContasReceber';
import ContasFinanceiras      from '../pages/ContasFinanceiras';
import BandeirasCartao        from '../pages/BandeirasCartao';
import Organizacoes           from '../pages/Organizacoes';
import CadastroOrganizacao    from '../pages/CadastroOrganizacao';
import CadastroUsuarios       from '../pages/CadastroUsuarios';
import PerfisUsuario          from '../pages/PerfisUsuario';
import NotasTecnicas          from '../pages/NotasTecnicas';
import TiposMovimentoFiscal   from '../pages/TiposMovimentoFiscal';
import LocalEstoque           from '../pages/LocalEstoque';
import ImportadorDados        from '../pages/ImportadorDados';
import Relatorios             from '../pages/Relatorios';
import PlanoContas            from '../pages/PlanoContas';
import DRE                   from '../pages/DRE';
import Devolucoes            from '../pages/Devolucoes';
import GerarRecorrencia      from '../pages/GerarRecorrencia';
import Kardex                from '../pages/Kardex';
import Auditoria             from '../pages/Auditoria';
import ModuloFiscal          from '../pages/ModuloFiscal';
import NotaFiscalManual      from '../pages/NotaFiscalManual';
import CadastrosTransporte   from '../pages/CadastrosTransporte';
import Agenda                from '../pages/Agenda';
import PainelSuporte         from '../pages/PainelSuporte';
import PainelClientesStandalone from '../pages/PainelClientesStandalone';

// Edição de organização por id dinâmico (/admin/organizacoes/editar/2, 3, …):
// o sistema de abas usa paths estáticos, então o id vem da URL corrente.
// key={id} remonta o cadastro ao trocar de organização na mesma aba.
function EditarOrganizacaoTab() {
  const location = useLocation();
  const m = location.pathname.match(/^\/admin\/organizacoes\/editar\/(\d+)/);
  const [id, setId] = useState(m ? m[1] : null);
  useEffect(() => { if (m && m[1] !== id) setId(m[1]); }, [location.pathname]); // eslint-disable-line
  return id ? <CadastroOrganizacao key={id} orgId={id} /> : null;
}

// "Minha Empresa" — BUG REAL corrigido: antes apontava pra
// /admin/organizacoes/editar/1 (organização 1 fixa, sempre), então um
// usuário operando em QUALQUER outra organização que clicasse "Minha
// Empresa" via ver/editar as configurações (inclusive NFC-e/NF-e/CSC) da
// organização 1 por engano — parecia "salvei numa organização e apareceu
// na outra", mas na real nunca tinha salvo na organização certa. Agora usa
// sempre a organização ATIVA da sessão (key={organizacao_id} força
// remontar ao trocar de loja, mesmo padrão do EditarOrganizacaoTab acima).
function MinhaEmpresaTab() {
  const { usuario } = useAuth();
  if (!usuario?.organizacao_id) return null;
  return <CadastroOrganizacao key={usuario.organizacao_id} orgId={usuario.organizacao_id} />;
}

// Versões mobile (modo completo do suporterb no celular — ver Layout#TabPanels):
// carregadas só quando usadas, pra não pesar o bundle do desktop.
const ProdutosMobileLazy = React.lazy(() => import('../pages/ProdutosMobile'));
const ClientesMobileLazy = React.lazy(() => import('../pages/ClientesMobile'));
const ContasReceberMobileLazy = React.lazy(() => import('../pages/ContasMobile').then(m => ({ default: m.ContasReceberMobile })));
const ContasPagarMobileLazy = React.lazy(() => import('../pages/ContasMobile').then(m => ({ default: m.ContasPagarMobile })));
const mobile = Tela => () => <React.Suspense fallback={null}><Tela /></React.Suspense>;

// ── Mapa rota → metadados + componente ─────────────────────────────
// A ordem importa: rotas mais específicas PRIMEIRO
export const ROTAS = [
  { path: '/produtos/cadastro',             label: 'Cad. Produtos',       icon: '📦', C: () => <CadastroProdutos />, M: mobile(ProdutosMobileLazy) },
  { path: '/produtos/alteracao-massa',      label: 'Alt. em Massa',       icon: '📦', C: () => <AlteracaoMassaProdutos /> },
  { path: '/produtos/grupos-marcas',        label: 'Grupos/Marcas',       icon: '📁', C: () => <GruposMarcas /> },
  { path: '/produtos',                      label: 'Produtos',            icon: '📦', C: () => <Produtos />, M: mobile(ProdutosMobileLazy) },
  { path: '/clientes/cadastro',             label: 'Cad. Clientes',       icon: '👥', C: () => <CadastroClientes />, M: mobile(ClientesMobileLazy) },
  { path: '/clientes',                      label: 'Clientes',            icon: '👥', C: () => <Clientes />, M: mobile(ClientesMobileLazy) },
  { path: '/tabelas-preco',                 label: 'Tab. de Preço',       icon: '📋', C: () => <TabelasPreco /> },
  { path: '/catalogo-online',               label: 'Catálogo Online',     icon: '🛍️', C: () => <CatalogoOnline /> },
  { path: '/mercadopago',                   label: 'Mercado Pago',        icon: '💳', C: () => <MercadoPagoConfig /> },
  { path: '/instagram',                     label: 'Instagram',           icon: '📷', C: () => <InstagramGestao /> },
  { path: '/fidelidade/extrato',            label: 'Extrato de Pontos',   icon: '⭐', C: () => <ExtratoFidelidade /> },
  { path: '/fidelidade/resumo',             label: 'Resumo de Pontos',    icon: '⭐', C: () => <ExtratoGeralFidelidade /> },
  { path: '/fidelidade',                    label: 'Fidelidade',          icon: '⭐', C: () => <FidelidadeConfig /> },
  { path: '/vendas/pedidos',                label: 'Pedidos/Orçamentos',  icon: '🛒', C: () => <PedidosOrcamentos /> },
  { path: '/vendas/devolucoes',             label: 'Devoluções',          icon: '↩', C: () => <Devolucoes /> },
  { path: '/vendas/recorrencia',            label: 'Gerar Recorrência',   icon: '🔁', C: () => <GerarRecorrencia /> },
  { path: '/vendas',                        label: 'Vendas',              icon: '🛒', C: () => <Vendas /> },
  { path: '/agenda',                        label: 'Agenda',              icon: '📅', C: () => <Agenda /> },
  { path: '/estoque/manutencao',            label: 'Manutenção Estoque',  icon: '🏭', C: () => <ManutencaoEstoque /> },
  { path: '/estoque/kardex',                label: 'Kardex',              icon: '📇', C: () => <Kardex /> },
  { path: '/estoque',                       label: 'Estoque',             icon: '🏭', C: () => <Estoque /> },
  { path: '/compras/fornecedores/cadastro', label: 'Cad. Fornecedores',   icon: '📦', C: () => <Fornecedores /> },
  { path: '/compras/fornecedores',          label: 'Fornecedores',        icon: '📦', C: () => <ListaFornecedores /> },
  { path: '/compras/importacao-dfe',        label: 'Importação DF-e',     icon: '📡', C: () => <EntradaNF abrirDfe /> },
  { path: '/compras/entradas-nf',           label: 'Entrada de NF',       icon: '📥', C: () => <EntradaNF /> },
  { path: '/financeiro/caixa',              label: 'Caixa',               icon: '🏦', C: () => <FechamentoCaixa /> },
  { path: '/financeiro/transferencia',      label: 'Movimentações',       icon: '💸', C: () => <TransferenciaContas /> },
  { path: '/financeiro/baixa-cartoes',      label: 'Baixa Cartões',       icon: '💳', C: () => <BaixaCartoes /> },
  { path: '/financeiro/contas-pagar',       label: 'Contas a Pagar',      icon: '💰', C: () => <ContasPagar />, M: mobile(ContasPagarMobileLazy) },
  { path: '/financeiro/contas-receber',     label: 'Contas a Receber',    icon: '💰', C: () => <ContasReceber />, M: mobile(ContasReceberMobileLazy) },
  { path: '/financeiro/contas',             label: 'Contas Financeiras',  icon: '🏦', C: () => <ContasFinanceiras /> },
  { path: '/financeiro/bandeiras',          label: 'Bandeiras de Cartão', icon: '💳', C: () => <BandeirasCartao /> },
  { path: '/financeiro/plano-contas',       label: 'Plano de Contas',     icon: '📒', C: () => <PlanoContas /> },
  { path: '/financeiro/dre',               label: 'DRE',                 icon: '📊', C: () => <DRE /> },
  { path: '/fiscal/documentos',            label: 'Módulo Fiscal',       icon: '🧾', C: () => <ModuloFiscal /> },
  { path: '/fiscal/nota-fiscal',           label: 'Nota Fiscal',         icon: '🧾', C: () => <NotaFiscalManual /> },
  { path: '/fiscal/mdfe/veiculos',         label: 'Veículos e Condutores (MDF-e)', icon: '🚚', C: () => <CadastrosTransporte /> },
  { path: '/admin/organizacoes/minha-empresa', label: 'Minha Empresa',    icon: '⚙️', C: () => <MinhaEmpresaTab /> },
  { path: '/admin/organizacoes/editar',     label: 'Editar Organização',  icon: '⚙️', C: () => <EditarOrganizacaoTab /> },
  { path: '/admin/organizacoes/novo',       label: 'Nova Organização',    icon: '⚙️', C: () => <CadastroOrganizacao /> },
  { path: '/admin/organizacoes',            label: 'Organizações',        icon: '⚙️', C: () => <Organizacoes /> },
  { path: '/admin/usuarios',               label: 'Usuários',            icon: '⚙️', C: () => <CadastroUsuarios /> },
  { path: '/admin/perfis',                 label: 'Perfis de Usuário',   icon: '⚙️', C: () => <PerfisUsuario /> },
  { path: '/admin/notas-tecnicas',         label: 'Notas Técnicas SEFAZ', icon: '📜', C: () => <NotasTecnicas /> },
  { path: '/admin/fiscal/tipos-movimento', label: 'Tipos de Movimento',  icon: '⚙️', C: () => <TiposMovimentoFiscal /> },
  { path: '/admin/locais-estoque',         label: 'Locais de Estoque',   icon: '⚙️', C: () => <LocalEstoque /> },
  { path: '/admin/importador',             label: 'Importar Dados',      icon: '📥', C: () => <ImportadorDados /> },
  { path: '/admin/auditoria',              label: 'Auditoria',           icon: '🔒', C: () => <Auditoria /> },
  { path: '/relatorios',                   label: 'Relatórios',          icon: '📈', C: () => <Relatorios /> },
  { path: '/assistente-ia',                label: 'Assistente IA',       icon: '🤖', C: () => <AssistenteIA /> },
  { path: '/falar-suporte',                label: 'Falar com Suporte',   icon: '💬', C: () => <FalarComSuporte /> },
  { path: '/dashboard',                    label: 'Dashboard',           icon: '📊', C: () => <Dashboard /> },
  { path: '/painel-suporte',               label: 'Backups (interno)',   icon: '🗄️', C: () => <PainelSuporte /> },
  // Login PRÓPRIO (painel_usuarios), sem relação com a sessão do ERP — ver
  // comentário em App.jsx e o atalho condicional em Sidebar.jsx (só
  // aparece em rbdigital/jcr; o backend barra qualquer outro host de
  // qualquer forma).
  { path: '/painel-clientes',              label: 'Painel de Clientes',  icon: '🧑‍💼', C: () => <PainelClientesStandalone /> },
];

function resolverRota(pathname) {
  return ROTAS.find(r => pathname === r.path || pathname.startsWith(r.path + '/')) || null;
}

const MAX_TABS = 15;
const TabContext = createContext(null);

export function TabProvider({ children }) {
  const [tabs,     setTabs]     = useState([]);
  const [activeId, setActiveId] = useState(null);

  const openTab = useCallback((rota) => {
    if (!rota) return;
    setTabs(prev => {
      const existe = prev.find(t => t.path === rota.path);
      if (existe) { setActiveId(existe.path); return prev; }
      const novas = [...prev, rota];
      if (novas.length > MAX_TABS) novas.shift();
      setActiveId(rota.path);
      return novas;
    });
    setActiveId(rota.path);
  }, []);

  const activateTab = useCallback((path) => setActiveId(path), []);

  const closeTab = useCallback((path, navigate) => {
    setTabs(prev => {
      const idx  = prev.findIndex(t => t.path === path);
      const rest = prev.filter(t => t.path !== path);
      if (path === activeId) {
        if (rest.length > 0) {
          const next = rest[Math.min(idx, rest.length - 1)];
          setActiveId(next.path);
          if (navigate) navigate(next.path);
        } else {
          setActiveId(null);
          if (navigate) navigate('/dashboard');
        }
      }
      return rest;
    });
  }, [activeId]);

  return (
    <TabContext.Provider value={{ tabs, activeId, openTab, activateTab, closeTab }}>
      {children}
    </TabContext.Provider>
  );
}

export function useTabContext() { return useContext(TabContext); }

// Observa mudanças de URL e abre/foca a aba correspondente.
// Também barra URL digitada de tela fora do perfil (Perfis de Usuário).
export function TabWatcher() {
  const location = useLocation();
  const navigate  = useNavigate();
  const { openTab } = useTabContext();
  const { pronto, permitido, rotaInicial } = usePermissoes();

  useEffect(() => {
    if (!pronto) return; // aguarda permissões antes de abrir qualquer aba
    if (location.pathname === '/' || location.pathname === '') {
      navigate(rotaInicial(), { replace: true });
      return;
    }
    const rota = resolverRota(location.pathname);
    if (!rota) return;
    if (!permitido(rota.path)) {
      const destino = rotaInicial();
      if (destino !== location.pathname) navigate(destino, { replace: true });
      return; // não abre a aba bloqueada
    }
    openTab(rota);
  }, [location.pathname, pronto]); // eslint-disable-line

  return null;
}
